import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { useTranslation } from 'react-i18next';
import { TCreateUserSelectedItem } from '../../../../../../../createUserState/createUserContext';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setIDsRestaurant: (value: string[]) => void;
	IDSelectedCustomer: TCreateUserSelectedItem;
	IDsSelectedRestaurant: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	setIDsRestaurant,
	IDSelectedCustomer,
	IDsSelectedRestaurant,
}) => {
	const [searchString, setSearchString] = useState<string>('');
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectOneRestaurant') }], [t]);

	const visibleData = useMemo(() => {
		const items = showArchived
			? data
			: data.filter((el) => el.status === EntityStatus.Active || IDsSelectedRestaurant.includes(el.id));

		return items.map((el) => ({
			...el,
			additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [data, showArchived, IDsSelectedRestaurant]);

	const filteredData = useMemo(() => {
		return searchString
			? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: visibleData;
	}, [searchString, visibleData]);

	const handleSelectAllData = useCallback(() => {
		if (visibleData.length === IDsSelectedRestaurant.length) {
			setIDsRestaurant([]);
		} else {
			const IDsOfAllRestaurants = visibleData.map((el) => el.id);
			setIDsRestaurant(IDsOfAllRestaurants);
		}
	}, [visibleData, IDsSelectedRestaurant, setIDsRestaurant]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={!!(visibleData.length && visibleData.length === IDsSelectedRestaurant.length)}
							disabled={!visibleData.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleData,
			IDsSelectedRestaurant,
			handleSelectAllData,
			searchString,
			setSearchString,
			showArchived,
			toggleShowArchived,
		],
	);

	useEffect(() => {
		setSearchString('');
	}, [IDSelectedCustomer]);

	return (
		<FormSelect
			testId="RestaurantsMultipleSelect"
			name="selectedRestaurants"
			label="restaurantsLabel"
			options={filteredData}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			disabled={!IDSelectedCustomer || isLoading}
			onChange={setIDsRestaurant}
			mode="multiple"
			counterValue={IDsSelectedRestaurant.length}
			validationRules={validationRules}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default React.memo(MultipleSelect);
